// import React from "react";
// import { CSVLink } from "react-csv";
// import { useTranslation } from "react-i18next";
// import { Button, IconButton } from "@mui/material";
// import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
// import dayjs from "dayjs";
//
// type ExcelProps = {
//     data: Record<string, any>[];
//     cols?: { label: string; key: string }[];
//     name: string;
//     classes?: string;
//     variant?: "icon" | "button";
//     buttonLabel?: string;
// };
//
// export const DownloadAsExcelSheet = (props: ExcelProps) => {
//     const { data = [], cols = [], name, classes, variant = "icon", buttonLabel } = props;
//     const name_with_timestamp = `${name} - ${dayjs().format("LLL")}`;
//     const { t } = useTranslation("downloadasexcelsheet");
//     const csvLinkRef = React.useRef<any>(null);
//
//     const handleDownload = () => {
//         if (csvLinkRef.current) {
//             csvLinkRef.current.link.click();
//         }
//     };
//
//     const sanitizedData = data.map((row) => {
//         const cleanRow: Record<string, any> = {};
//         Object.keys(row || {}).forEach((key) => {
//             cleanRow[key] = row[key] ?? "";
//         });
//         return cleanRow;
//     });
//
//     const validHeaders =
//         cols?.filter(
//             (h) => h && typeof h.label === "string" && typeof h.key === "string"
//         ) || [];
//
//     const csvLink = (
//         <CSVLink
//             id="trigger-download"
//             data={data}
//             headers={validHeaders}
//             filename={name_with_timestamp}
//             ref={csvLinkRef}
//             style={{ display: "none" }}
//         />
//     );
//
//     if (variant === "button") {
//         return (
//             <>
//                 <Button
//                     variant="outlined"
//                     size="small"
//                     startIcon={<FileDownloadOutlinedIcon />}
//                     onClick={handleDownload}
//                     sx={{
//                         textTransform: "none",
//                         fontWeight: 600,
//                         borderColor: "#D1D5DB",
//                         color: "#374151",
//                         "&:hover": { borderColor: "#9CA3AF", backgroundColor: "#F9FAFB" },
//                     }}
//                 >
//                     {buttonLabel || t("Download") || "Export"}
//                 </Button>
//                 {csvLink}
//             </>
//         );
//     }
//
//     return (
//         <div
//             className={`flex flex-row items-center justify-between gap-x-4 ${classes || ""}`}
//         >
//             <IconButton onClick={handleDownload} color="warning" size="medium">
//                 <i
//                     className="fa-solid fa-download cursor-pointer text-xl md:text-3xl"
//                     title={t("Download") || "Download"}
//                 />
//             </IconButton>
//             {csvLink}
//         </div>
//     );
// };

import React from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { Button, ButtonProps, IconButton } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import dayjs from "dayjs";

type ExcelProps = {
    data: Record<string, any>[];
    cols?: { label: string; key: string }[];
    name: string;
    classes?: string;
    variant?: "icon" | "button";
    buttonLabel?: string;

    // Optional - existing usages won't be affected
    buttonProps?: ButtonProps;
};

export const DownloadAsExcelSheet = (props: ExcelProps) => {
    const { data = [], cols = [], name, classes, variant = "icon", buttonLabel, buttonProps } = props;

    const name_with_timestamp = `${name} - ${dayjs().format("LLL")}`;
    const { t } = useTranslation("downloadasexcelsheet");
    const csvLinkRef = React.useRef<any>(null);

    const handleDownload = () => {
        if (csvLinkRef.current) {
            csvLinkRef.current.link.click();
        }
    };

    const sanitizedData = data.map((row) => {
        const cleanRow: Record<string, any> = {};
        Object.keys(row || {}).forEach((key) => {
            cleanRow[key] = row[key] ?? "";
        });
        return cleanRow;
    });

    const validHeaders =
        cols?.filter((h) => h && typeof h.label === "string" && typeof h.key === "string") || [];

    const csvLink = (
        <CSVLink
            id="trigger-download"
            data={sanitizedData}
            headers={validHeaders}
            filename={name_with_timestamp}
            ref={csvLinkRef}
            style={{ display: "none" }}
        />
    );

    if (variant === "button") {
        return (
            <>
                <Button
                    variant={buttonProps?.variant ?? "outlined"}
                    size={buttonProps?.size ?? "small"}
                    startIcon={buttonProps?.startIcon ?? <FileDownloadOutlinedIcon />}
                    onClick={handleDownload}
                    {...buttonProps}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "#D1D5DB",
                        color: "#374151",
                        "&:hover": {
                            borderColor: "#9CA3AF",
                            backgroundColor: "#F9FAFB",
                        },
                        ...buttonProps?.sx,
                    }}
                >
                    {buttonLabel || t("Download") || "Export"}
                </Button>
                {csvLink}
            </>
        );
    }

    return (
        <div className={`flex flex-row items-center justify-between gap-x-4 ${classes || ""}`}>
            <IconButton onClick={handleDownload} color="warning" size="medium">
                <i
                    className="fa-solid fa-download cursor-pointer text-xl md:text-3xl"
                    title={t("Download") || "Download"}
                />
            </IconButton>
            {csvLink}
        </div>
    );
};
